import type {PreviewPlansQueryVariables} from 'web-app/react/plans/manage-plan/__generated__/queries.api-types';
import {BillingSchemeName, FinancePeriod, type PlanInput} from 'web-app/util/graphql/api-types';

import type {Discount, Part, Plan, DiscountPeriod} from './plan-types';

/**
 * Maps DiscountPeriod to FinancePeriod . Defaults to MONTHLY for any unknown periods.
 */
const mapDiscountPeriodToFinancePeriod = (period: DiscountPeriod): FinancePeriod => {
    switch (period) {
        case 'DAILY':
            return FinancePeriod.DAILY;
        case 'HOURLY':
            return FinancePeriod.HOURLY;
        case 'WEEKLY':
            return FinancePeriod.WEEKLY;
        case 'YEARLY':
            return FinancePeriod.YEARLY;
        case 'MONTHLY':
        default:
            return FinancePeriod.MONTHLY;
    }
};

export const transformApiDiscountToPriceModifier = (
    discount: Discount,
): PreviewPlansQueryVariables['planInputs'][number]['planParts'][number]['priceModifiers'][number] => {
    if (discount.discountPresetId) {
        return {
            presetDiscount: {
                discountPresetId: discount.discountPresetId,
                ordering: discount.ordering,
                period: mapDiscountPeriodToFinancePeriod(discount.period),
            },
        };
    }
    return {
        discount: {
            amount: discount.amount,
            percent: discount.isPercent,
            ordering: discount.ordering,
            period: mapDiscountPeriodToFinancePeriod(discount.period),
            showOnInvoice: discount.showOnInvoice,
            title: discount.title,
        },
    };
};

const transformPlanPartToPlanPartInput = (
    part: Part,
): PreviewPlansQueryVariables['planInputs'][number]['planParts'][number] => {
    return {
        ordering: part.ordering,
        billingProfileId: part.billingProfileId,
        sessionBookings: part.sessionBookings.map(sessionBooking => ({
            allowFunding: sessionBooking.allowFunding ?? false,
            dayOfWeek: sessionBooking.day,
            sessionId: sessionBooking.sessionId,
            range: {
                from: sessionBooking.start ?? '',
                to: sessionBooking.end ?? '',
            },
        })),
        productBookings: part.productBookings.map(productBooking => ({
            amount: productBooking.amount,
            dayOfWeek: productBooking.day,
            productId: productBooking.productId,
            bookedPrice: productBooking.bookedPrice,
            planRuleId: productBooking.planRuleId,
        })),
        termTimeOnly: part.termTimeOnly,
        attendanceScheduleId: part.attendanceScheduleId ?? '',
        billingScheme: part.billing.id === 'MONTHLY' ? BillingSchemeName.MONTHLY : BillingSchemeName.ACTUALS_V2,
        invoices: part.billing.invoices,
        priceModifiers: part.discounts.map(transformApiDiscountToPriceModifier),
        weeksOfCare: part.billing.weeksOfCare,
    };
};

const transformPlanToPlanInput = (plan: Plan): PlanInput => {
    const pricing = plan.pricingGroupId
        ? {
              pricingGroupId: plan.pricingGroupId,
              ruleGroupId: null,
          }
        : {
              pricingGroupId: null,
              ruleGroupId: plan.ruleGroupId,
          };

    return {
        childId: plan.childId,
        oldPlanId: plan.planId,
        planParts: plan.planParts.map(part => transformPlanPartToPlanPartInput(part)),
        pricing,
        range: {
            from: plan.from,
            to: plan.to,
        },
    };
};

export {transformPlanToPlanInput, transformPlanPartToPlanPartInput};
